import { backHome } from "./quizState"
import { buildRef } from "./selection"
import type { AnswerResult } from "./state"
import { state } from "./state"
import { letterFor } from "./util"

type MissedEntry = AnswerResult & { qIdx: number }

export function Results() {
  const total = state.allQuestions.length
  const missed: MissedEntry[] = Object.entries(state.results)
    .filter(([, r]) => !r.correct)
    .map(([k, r]) => ({ qIdx: parseInt(k, 10), ...r }))
    .sort((a, b) => a.qIdx - b.qIdx)
  const right = Object.values(state.results).filter((r) => r.correct).length
  const pct = total ? Math.round((right / total) * 100) : 0
  const tier = pct < 80 ? "fail" : pct < 90 ? "caution" : "pass"
  const stampText = pct < 80 ? "Try Again" : pct < 90 ? "Barely Passed" : "Passed"

  return (
    <>
      <div class="score-block">
        <div class="score-label">— Final Score</div>
        <div class={`score-pct ${tier}`}>{pct}%</div>
        <div>
          <div class={`score-stamp ${tier}`}>{stampText}</div>
          {tier === "caution" && (
            <div class="caution-note">
              This clears the passing bar, but only just. Review the items below before you take the real exam.
            </div>
          )}
        </div>
        <div class="score-detail">
          {right} of {total} correct · 80% needed on the real exam
        </div>
      </div>

      <div>
        {missed.length === 0 ? (
          <div class="perfect-msg">A clean sheet. Every answer correct — you're ready.</div>
        ) : (
          <>
            <div class="missed-label">Review</div>
            <div class="missed-hint">
              {missed.length === 1 ? "One to study" : `${missed.length} to study`}. Each references the page in the
              handbook where the answer is explained.
            </div>
            {missed.map((a) => (
              <MissedItem key={a.qIdx} a={a} />
            ))}
          </>
        )}
      </div>

      <div class="results-actions">
        <button type="button" class="btn primary" onClick={backHome}>
          ← Home
        </button>
      </div>
    </>
  )
}

function MissedItem({ a }: { a: MissedEntry }) {
  const q = state.allQuestions[a.qIdx]
  const r = buildRef(q)
  const options = state.shuffledChoices[a.qIdx] ?? []
  const correctDisplayIdx = options.indexOf(q.answer.text)
  return (
    <div class="missed-item">
      <div class="missed-q">{q.q}</div>
      <div class="missed-answer wrong">
        <span class="letter">{letterFor(a.given)}</span>
        You answered: <strong>{a.givenText}</strong>
      </div>
      <div class="missed-answer right">
        <span class="letter">{letterFor(correctDisplayIdx)}</span>
        Correct: <strong>{q.answer.text}</strong>
      </div>
      <div class="missed-ref">
        — Section {r.section} — {r.topic} · Page {r.page}
      </div>
    </div>
  )
}
